<template>
    <BaseModal
        :title="`${translations.cancellation.cancellation} - ${translations.cancellationReason.reason}`"
        class="cancellation-reason-modal"
        size="modal-lg"
        :closable="true"
        ref="baseModalComponent"
    >
        <template v-slot:body>
            <form class="kt-form kt-form--label-right" @submit.prevent="submitForm" ref="formElement">
                <div class="cancellation-reasons-list kt-radio-list">
                    <label
                        v-for="cancellationReason in cancellationReasons"
                        :key="cancellationReason.value"
                        class="cancellation-reason-option kt-radio"
                        :class="{ 'kt-radio--success': cancellationReason.value === selectedReason }"
                    >
                        <input
                            type="radio"
                            name="cancellationReason"
                            :value="cancellationReason.value"
                            v-model="selectedReason"
                        />
                        <span></span>
                        {{ translations.enums.cancellationReason[cancellationReason.name] }}
                    </label>
                </div>
            </form>
        </template>
        <template v-slot:footer>
            <button type="button" class="btn btn-secondary" @click="onGoBack">{{ translations.general.back }}</button>

            <button
                type="button"
                class="btn btn-success"
                :class="{ [style.buttonLoaderClass]: isSaving }"
                :disabled="isSubmitDisabled"
                @click="submitForm"
            >
                {{ translations.general.confirm }}
            </button>
        </template>
    </BaseModal>
</template>

<script setup lang="ts">
    import type { IAddCancellationCmd } from "@/data/commands/order/AddCancellationCmd";
    import { Factory } from "@/data/dao/Factory";
    import { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { OrderDao } from "@/data/dao/service/OrderDao";
    import { CancellationReason } from "@/data/model/Enums";
    import { CancellationMode } from "@/data/model/Enums";
    import { exceptionTypes } from "@/data/util/Config";
    import { Misc } from "@/data/util/Misc";
    import { UiNotification } from "@/portal/ui/Notification";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { style } from "@/shared/ui/Style";
    import { computed, onMounted, ref } from "vue";

    import BaseModal from "@/components/dialog/BaseModal.vue";

    const emit = defineEmits(["goBack", "saveStart", "saveError", "saveEnd"]);

    const props = defineProps<{
        locationId: string;
        orderId: string;
        cancellableAmount: number;
        data: IAddCancellationCmd;
        onSaveStart?: () => void;
        onSaveError?: (errorCode: string, errorMessage: string) => void;
        onSaveEnd?: () => void;
    }>();

    const dao = Factory.getInstance<OrderDao>(OrderDao);

    // define html refs
    const baseModalComponent = ref<InstanceType<typeof BaseModal> | null>(null);
    const formElement = ref<HTMLFormElement | null>(null);
    const isSaving = ref(false);
    const isSubmitDisabled = computed(() => !isInitialized.value || isSaving.value || selectedReason.value === null);
    const isInitialized = ref(false);
    const hasAvailableItemsWithStockManagement = ref(false);
    const shouldUpdateStock = ref<boolean | undefined>(undefined);

    // Define the reasons object from the enum
    const cancellationReasons = ref(Misc.enumsToValueNameArray(CancellationReason));

    // Variable to track the selected reason
    const selectedReason = ref<CancellationReason | null>(null);

    function onGoBack() {
        emit("goBack");
        close();
    }

    async function submitForm() {
        if (isStockChangeApprovalNeeded(selectedReason.value!)) {
            showConfirmStockChangePopover();
        } else {
            showCancellationConfirmPopover();
        }
    }

    function isStockChangeApprovalNeeded(reason: CancellationReason) {
        const reasonsWithStockChangeApproval = [
            CancellationReason.IncorrectIncompleteItemInformation,
            CancellationReason.OrderedIncorrectlyEndUser,
            CancellationReason.OrderedIncorrectlyStaffMember,
            CancellationReason.EndUserChangedMind,
            CancellationReason.PriceWrong,
            CancellationReason.LateFulfilment,
            CancellationReason.Other,
        ];

        return hasAvailableItemsWithStockManagement.value && reasonsWithStockChangeApproval.includes(reason);
    }

    function showConfirmStockChangePopover() {
        UiNotification.confirm(
            "",
            translations.cancellation.canStockBeReused,
            () => {
                shouldUpdateStock.value = true;
                showCancellationConfirmPopover();
            },
            translations.general.yes,
            () => {
                shouldUpdateStock.value = false;
                showCancellationConfirmPopover();
            },
            translations.general.no,
            undefined,
            "btn btn-danger"
        );
    }

    function showCancellationConfirmPopover() {
        UiNotification.confirm(
            translations.cancellation.confirmMessage.replace("{{amt}}", props.cancellableAmount.toFixed(2)),
            translations.cancellation.confirmTitle,
            () => {
                addCancellation();
            },
            translations.cancellation.submitBtn,
            undefined,
            translations.cancellation.notNow
        );
    }

    async function addCancellation() {
        if (props.onSaveStart) {
            props.onSaveStart();
        }
        isSaving.value = true;

        try {
            const addCancellationCommand = {
                ...props.data,
                reason: selectedReason.value!,
                updateStock: shouldUpdateStock.value,
            };
            await dao.addCancellation(props.orderId, addCancellationCommand);

        //    emit("saveEnd");
            if (props.onSaveEnd) {
                props.onSaveEnd();
            }
        } catch (ex) {
            logError(ex);

            isSaving.value = false;

            let errorMessage = translations.cancellation.cancellationFailed;
            let errorCode = "unknown";
            if (ex instanceof DaoError) {
                errorCode = ex.type;
                if (ex.type === exceptionTypes.cancellationNotAllowedForPayment) {
                  errorMessage = translations.cancellation.cancellationNotAllowedForPayment;
                } else if (ex.type === exceptionTypes.singleCancellationFailedException) {
                  errorMessage = translations.cancellation.singleCancellationFailed;
                } else if (ex.type === exceptionTypes.multipleCancellationsFailedException) {
                  errorMessage = translations.cancellation.multipleCancellationsFailed;
                } else if (ex.type === exceptionTypes.refundOutcomeUnknownException) {
                  errorMessage = translations.cancellation.refundOutcomeUnknown;
                } else if (ex.type === exceptionTypes.multiplePdPaymentCancellationsNotSupportedException) {
                  errorMessage = translations.cancellation.multiplePdPaymentCancellationsNotSupported;
                } else if (ex.type === exceptionTypes.twintOnlineRefundNotSupportedException) {
                    errorMessage = translations.cancellation.twintOnlineRefundNotSupported;
                }
            }
            
            if (props.onSaveError) {
                props.onSaveError(errorCode, errorMessage);
            }
        } finally {
            close();
        }
    }

    async function loadStockManagementStatus() {
        if (props.data.mode == CancellationMode.Amount) {
            hasAvailableItemsWithStockManagement.value = false;
            isInitialized.value = true;
            return;
        }

        try {
            const status = await dao.getOrderStockManagementStatus(
                props.orderId,
                props.data.mode,
                props.data.positions
            );
            hasAvailableItemsWithStockManagement.value = status.hasAvailableItemsWithStockManagement;
            isInitialized.value = true;
        } catch (ex) {
            logError(ex);
            UiNotification.showException(translations.general.failedLoadingData);
            close();
        }
    }

    onMounted(async () => {
        await loadStockManagementStatus();
    });

    // define exposed
    function show() {
        baseModalComponent.value?.show();
    }

    function close() {
        baseModalComponent.value?.close();
    }

    defineExpose({
        show,
        close,
    });
</script>

<style lang="scss">
    .modal.cancellation-reason-modal {
        z-index: 1051;

        + .modal-backdrop {
            z-index: 1050;
        }

        .cancellation-reasons-list {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding: 0 1.5em;

            .cancellation-reason-option {
                user-select: none;
                padding-top: 5px;
                padding-bottom: 5px;
                margin: 0;

                > span {
                    top: 6px;
                }
            }
        }

        .btn[disabled] {
            cursor: default;
        }
    }
</style>
